/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
"use client";

import { LeadThrough, TestDriveRequestCreateDTO, TestDriveRequestStatus } from "@alba-cars/common-modules";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { DatePicker } from "@/components/common/date/DatePicker";
import FormPhoneField from "@/components/common/forms/FormPhoneField";
import FormTextField from "@/components/common/forms/FormTextField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { createTestDriveRequest } from "@/lib/api/test-drive-request/create-test-drive-request";
import { cn } from "@/lib/utils";
import successAnimation from "@/public/assets/animations/success.json";
import { testDriveSchema } from "@/schemas/validation/testDriveFormSchema";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
interface TestDriveModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
}

const generateTimeSlots = (startHour: number, endHour: number) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const formattedHour = hour <= 12 ? hour : hour - 12;
    const amPm = hour < 12 ? "am" : "pm";
    slots.push(`${formattedHour}:00 ${amPm}`);
  }
  return slots;
};

const isTimeSlotDisabled = (timeSlot: string, selectedDate: Date) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

  // If selected date is in the future, no slots are disabled
  if (selectedDay.getTime() > today.getTime()) {
    return false;
  }

  // If it's today, check if the time slot is in the past
  if (selectedDay.getTime() === today.getTime()) {
    const [hourStr, minuteStr, amPm] = timeSlot.split(/[: ]/);
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    // Convert to 24-hour format
    if (amPm.toLowerCase() === "pm" && hour < 12) {
      hour += 12;
    } else if (amPm.toLowerCase() === "am" && hour === 12) {
      hour = 0;
    }

    // Create a date object for the time slot
    const slotTime = new Date(selectedDay);
    slotTime.setHours(hour, minute, 0, 0);

    return slotTime <= now;
  }

  return false;
};

// Function to update the date with the selected time
const updateDateWithTime = (date: string, timeSlot: string) => {
  const selectedDate = new Date(date);
  const [hourStr, minuteStr, amPm] = timeSlot.split(/[: ]/);
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  // Convert to 24-hour format
  if (amPm.toLowerCase() === "pm" && hour < 12) {
    hour += 12;
  } else if (amPm.toLowerCase() === "am" && hour === 12) {
    hour = 0;
  }

  selectedDate.setHours(hour, minute, 0, 0);
  return selectedDate.toISOString();
};

const timeSlots = generateTimeSlots(10, 21);

interface FormData extends TestDriveRequestCreateDTO {
  selectedTime: string;
}

const TestDriveModal = ({ isOpen, onClose, vehicleId }: TestDriveModalProps) => {
  const { isAuthLoading, isAuthenticated, user } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successDate, setSuccessDate] = useState<Date | null>(null);

  const handleClose = () => {
    setShowSuccess(false);
    setSuccessDate(null);
    onClose();
  };

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(testDriveSchema),
    defaultValues: {
      vehicle: { existingId: vehicleId },
      status: TestDriveRequestStatus.NOT_CONTACTED,
      leadThrough: LeadThrough.WEBSITE,
      requestedDate: new Date().toISOString(),
      selectedTime: timeSlots[0],
    },
  });

  const resetForm = useCallback(() => {
    const now = new Date();
    const firstValidSlot = timeSlots.find((slot) => !isTimeSlotDisabled(slot, now)) || timeSlots[0];
    reset({
      vehicle: { existingId: vehicleId },
      status: TestDriveRequestStatus.NOT_CONTACTED,
      leadThrough: LeadThrough.WEBSITE,
      requestedDate: updateDateWithTime(now.toISOString(), firstValidSlot),
      selectedTime: firstValidSlot,
      testerName: isAuthenticated && user ? user.name || "" : "",
      testerMobile: isAuthenticated && user ? user.phone || "" : "",
    });
  }, [isAuthenticated, user, vehicleId, reset]);

  const requestedDate = watch("requestedDate");

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const createTestDriveRequestMutation = useMutation({
    mutationFn: async (data: FormData) => await createTestDriveRequest(data),
    onSuccess: (_, data) => {
      const date = new Date(data.requestedDate);
      setSuccessDate(date);
      setShowSuccess(true);
      resetForm();
    },
    onError: (_error) => {
      handleClose();
      toast.error("Error submitting form");
    },
  });

  const onSubmit = async (data: FormData) => {
    await createTestDriveRequestMutation.mutateAsync(data);
  };

  if (showSuccess && successDate) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-white text-black sm:max-w-2xl">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl font-semibold text-black">Test Drive Request Submitted</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full flex-col items-center justify-center">
            <div className="h-64 w-64">
              <Lottie animationData={successAnimation} loop={false} autoplay />
            </div>
            <div className="w-full space-y-2 rounded-lg bg-gray-50 p-4">
              <p className="inter text-sm font-medium text-secondary-foreground">Your Test Drive is Scheduled for:</p>
              <div className="flex flex-col items-center space-y-2 md:flex-row md:justify-between md:space-y-0">
                <div className="flex items-center space-x-2">
                  <span className="inter text-base font-semibold text-primary-foreground">
                    {successDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inter text-base font-medium text-primary">
                    at {successDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              </div>
            </div>
            <p className="inter mt-4 text-center text-lg font-semibold text-primary-foreground">
              Your test drive request has been submitted successfully! <br /> We will get in touch with you soon.
            </p>
            <button
              className="mt-4 rounded-lg bg-primary px-6 py-3 text-center text-sm font-semibold text-white transition-all duration-300 hover:cursor-pointer hover:bg-primary-dark disabled:opacity-50"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="no-scrollbar max-h-[100vh] overflow-y-scroll bg-white text-black sm:max-w-2xl">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl font-semibold text-black">Let&apos;s Test Drive</DialogTitle>
          <p className="text-sm text-gray-600">Visit our Showroom and Experience the Car of your dreams</p>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormTextField
            name="testerName"
            control={control}
            placeholder={isAuthLoading ? "Loading user information..." : "Enter your name"}
            disabled={isAuthLoading || isAuthenticated}
            rules={{
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters",
              },
              maxLength: {
                value: 255,
                message: "Name must be less than 255 characters",
              },
            }}
          />

          <FormPhoneField
            name="testerMobile"
            control={control}
            placeholder={isAuthLoading ? "Loading user information..." : "Enter your mobile number"}
            disabled={isAuthLoading || isAuthenticated}
            rules={{
              required: "Mobile number is required",
              minLength: {
                value: 8,
                message: "Mobile number must be at least 8 characters",
              },
              maxLength: {
                value: 20,
                message: "Mobile number must be less than 20 characters",
              },
            }}
          />

          <div className="space-y-2">
            <span className="inter text-sm font-bold text-primary-foreground">Select Date & Time</span>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-2">
                <Controller
                  name="requestedDate"
                  control={control}
                  rules={{ required: "Please select a date" }}
                  render={({ field }) => (
                    <DatePicker
                      selectedDate={field.value ? new Date(field.value) : new Date()}
                      onChange={(date) => {
                        field.onChange(date?.toISOString());
                      }}
                      minDate={new Date()}
                    />
                  )}
                />
              </div>

              <div className="flex-1">
                <Controller
                  name="requestedDate"
                  control={control}
                  render={({ field }) => (
                    <p className="mb-2 text-sm font-bold text-gray-900">
                      {new Date(field.value).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  )}
                />
                <p className="font-small mb-2 text-sm">Time you&apos;re available</p>
                <div className="no-scrollbar mt-4 max-h-[200px] overflow-y-auto md:grid md:grid-cols-1 md:gap-1.5">
                  <Controller
                    name="selectedTime"
                    control={control}
                    rules={{ required: "Please select a time" }}
                    render={({ field }) => (
                      <div className="flex space-x-2 md:flex-col md:space-x-0 md:space-y-3">
                        {timeSlots.map((time) => {
                          const isDisabled = isTimeSlotDisabled(time, new Date(requestedDate));
                          return (
                            <div
                              key={time}
                              onClick={() => {
                                if (!isDisabled) {
                                  field.onChange(time);
                                  setValue("requestedDate", updateDateWithTime(requestedDate, time));
                                }
                              }}
                              className={cn(
                                "w-full border hover:cursor-pointer",
                                "rounded-lg px-3 py-1.5 text-center text-sm font-medium transition-all duration-300",
                                isDisabled
                                  ? "border-gray-300 bg-gray-100 text-gray-400 hover:cursor-not-allowed"
                                  : field.value === time
                                    ? "bg-primary text-white hover:bg-primary-dark"
                                    : "text-primary hover:bg-gray-50 hover:text-primary-dark",
                              )}
                            >
                              {time}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="inter mt-4 w-full rounded-lg bg-primary py-3 text-center text-sm font-semibold text-white transition-all duration-300 hover:cursor-pointer hover:bg-primary-dark disabled:opacity-50"
            disabled={createTestDriveRequestMutation.isPending}
          >
            {createTestDriveRequestMutation.isPending ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading
              </div>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TestDriveModal;
